import { getParamFromUrl } from './checkoutRedirects';
import {
    trackEvent,
    eventNameConstants,
    interactionTypeConstants,
    stepInteractionConstants,
} from 'dibs-tracking';
import { type Pane, PANES } from '../constants/checkoutConstants';
import { filterNulls } from 'dibs-ts-utils/exports/filterNulls';
import {
    type StripeExpressCheckoutElementReadyEvent,
    type ExpressPaymentType,
} from '@stripe/stripe-js';

type StripeExpressPaymentMethods = NonNullable<
    StripeExpressCheckoutElementReadyEvent['availablePaymentMethods']
>;

//Add GA4 variables eventName and step_type where appropriate
export const PANE_EVENT_DICTIONARY = {
    [PANES.PANE_SHIPPING]: { eventName: 'add_shipping_info', stepType: 'shipping address' },
    [PANES.PANE_DELIVERY]: { eventName: 'add_shipping_info', stepType: 'shipping method' },
    [PANES.PANE_PAYMENT]: { eventName: 'add_payment_info', stepType: 'payment method' },
    [PANES.PANE_REVIEW]: { eventName: 'order_review', stepType: 'order review' },
};

export function getCheckoutTriggerPage(referrer: string, isUserComesFromEmail: boolean): string {
    if (isUserComesFromEmail) {
        return 'email';
    }

    if (!referrer) {
        return 'unknown';
    }

    const qIndex = referrer.indexOf('?');
    if (qIndex !== -1) {
        // strip query params from referrer url
        // for example, don't treat a pdp with an AB test param that includes checkout as a checkout url
        // https://www.1stdibs.com/id-f_38507712/?abtests=checkout%3Dvariant
        referrer = referrer.slice(0, qIndex);
    }

    if (referrer.indexOf('/favorites/') !== -1) {
        return 'favorites';
    } else if (referrer.indexOf('/inbox/') !== -1) {
        return 'message center';
    } else if (referrer.indexOf('/orders/') !== -1) {
        return 'my orders';
    } else if (referrer.indexOf('/cart/') !== -1) {
        return 'cart';
    } else if (referrer.indexOf('id-') !== -1 && referrer.indexOf('checkout') === -1) {
        return 'pdp';
    } else if (referrer.indexOf('/similar-sold/') !== -1) {
        return 'similar sold items';
    }

    if (referrer.includes('1stdibs.com')) {
        return 'other';
    }

    return 'external';
}

function getCheckoutTriggerFromReferrer(referrer: string, isUserComesFromEmail: boolean): string {
    const triggerPageType = getParamFromUrl('triggerPage');
    if (triggerPageType) {
        return triggerPageType;
    }

    return getCheckoutTriggerPage(referrer, isUserComesFromEmail);
}

export function getCheckoutTrigger(
    isNegotiableOrder: boolean,
    isUserComesFromEmail: boolean,
    isUserComesFromAcceptedOffer: boolean,
    referrer: string = document.referrer
): string {
    const triggerType = getParamFromUrl('triggerType');

    if (triggerType) {
        return triggerType;
    }

    const triggers = [];
    const trigger = getCheckoutTriggerFromReferrer(referrer, isUserComesFromEmail);
    if (trigger) {
        triggers.push(trigger);

        if (isNegotiableOrder) {
            triggers.push('make offer');
        } else if (getParamFromUrl('fullPrice') === 'true') {
            triggers.push('purchase at full price');
        } else if (isUserComesFromAcceptedOffer) {
            triggers.push('accept offer');
        } else {
            triggers.push('purchase');
        }
    }

    return triggers.join('-');
}

type StepInteractionKey = keyof typeof stepInteractionConstants;
type StepInteractionValue = (typeof stepInteractionConstants)[StepInteractionKey];
type CurrentPane = Pane;

function trackPromoInteraction({
    stepInteractionName,
    currentPane,
    promoCode,
}: {
    stepInteractionName: StepInteractionValue;
    currentPane: CurrentPane;
    promoCode?: string;
}): void {
    trackEvent({
        eventName: eventNameConstants.EVENT_PROMO_INTERACTION,
        interaction_type: interactionTypeConstants.INTERACTION_TYPE_CHECKOUT,
        step_interaction_name: stepInteractionName,
        step_type: currentPane,
        trigger: promoCode,
    });
}

export function trackPromoCodeDisplay({ currentPane }: { currentPane: CurrentPane }): void {
    trackPromoInteraction({
        stepInteractionName: stepInteractionConstants.STEP_INTERACTION_DISPLAY,
        currentPane,
    });
}

export function trackPromoCodeClick({ currentPane }: { currentPane: CurrentPane }): void {
    trackPromoInteraction({
        stepInteractionName: stepInteractionConstants.STEP_INTERACTION_CLICK,
        currentPane,
    });
}

export function trackPromoCodeApply({
    successfullyApplied,
    currentPane,
    promoCode,
}: {
    successfullyApplied: boolean;
    currentPane: CurrentPane;
    promoCode: string;
}): void {
    trackPromoInteraction({
        stepInteractionName: successfullyApplied
            ? stepInteractionConstants.STEP_INTERACTION_SUCCESS
            : stepInteractionConstants.STEP_INTERACTION_FAIL,
        currentPane,
        promoCode,
    });
}

export function trackSummaryToggle(isExpanded: boolean, currentStep: CurrentPane): void {
    trackEvent({
        eventName: eventNameConstants.EVENT_CHECKOUT_INTERACTION,
        interaction_type: interactionTypeConstants.INTERACTION_TYPE_ORDER_SUMMARY_DRAWER,
        step_interaction_name: isExpanded
            ? stepInteractionConstants.STEP_INTERACTION_EXPAND
            : stepInteractionConstants.STEP_INTERACTION_COLLAPSE,
        step_type: currentStep,
    });
}

export function trackExpressCheckoutDisplay({
    availablePaymentMethods,
}: {
    availablePaymentMethods: StripeExpressPaymentMethods;
}): void {
    trackEvent({
        eventName: eventNameConstants.EVENT_CHECKOUT_INTERACTION,
        interaction_type: interactionTypeConstants.INTERACTION_TYPE_EXPRESS_CHECKOUT,
        step_interaction_name: stepInteractionConstants.STEP_INTERACTION_EXPRESS_CHECKOUT_DISPLAY,
        trigger: Object.entries(availablePaymentMethods)
            .map(([key, value]) => {
                if (value) {
                    return key.replaceAll('_', '').toLowerCase();
                }
                return null;
            })
            .filter(filterNulls)
            .join('|'),
    });
}

export function trackExpressCheckoutClick({
    expressPaymentType,
}: {
    expressPaymentType: ExpressPaymentType;
}): void {
    trackEvent({
        eventName: eventNameConstants.EVENT_CHECKOUT_INTERACTION,
        interaction_type: interactionTypeConstants.INTERACTION_TYPE_EXPRESS_CHECKOUT,
        step_interaction_name: stepInteractionConstants.STEP_INTERACTION_EXPRESS_CHECKOUT_CLICK,
        trigger: expressPaymentType.replaceAll('_', '').toLowerCase(),
    });
}

export function trackExpressCheckoutExitClick(): void {
    trackEvent({
        eventName: eventNameConstants.EVENT_CHECKOUT_INTERACTION,
        interaction_type: interactionTypeConstants.INTERACTION_TYPE_EXPRESS_CHECKOUT,
        step_interaction_name:
            stepInteractionConstants.STEP_INTERACTION_EXPRESS_CHECKOUT_EXIT_CLICK,
    });
}

export function trackRequestQuoteSubmit(): void {
    trackEvent({
        eventName: eventNameConstants.EVENT_CONTACT_DEALER,
        interaction_type: interactionTypeConstants.INTERACTION_TYPE_CONTACT_DEALER,
        step_interaction_name: stepInteractionConstants.STEP_INTERACTION_COMPLETE,
        trigger: 'request a shipping quote - checkout submit',
    });
}

export function trackCheckoutItemImageClick(): void {
    trackEvent({
        eventName: eventNameConstants.EVENT_CHECKOUT_INTERACTION,
        interaction_type: interactionTypeConstants.INTERACTION_TYPE_ITEM_THUMBNAIL,
        step_interaction_name: stepInteractionConstants.STEP_INTERACTION_CLICK,
    });
}

export function trackCheckoutItemImageChange(): void {
    trackEvent({
        eventName: eventNameConstants.EVENT_CHECKOUT_INTERACTION,
        interaction_type: interactionTypeConstants.INTERACTION_TYPE_ITEM_IMAGE_CHANGE,
        step_interaction_name: stepInteractionConstants.STEP_INTERACTION_CLICK,
    });
}

export function trackCheckoutItemImageViewPdp(): void {
    trackEvent({
        eventName: eventNameConstants.EVENT_CHECKOUT_INTERACTION,
        interaction_type: interactionTypeConstants.INTERACTION_TYPE_VIEW_PDP,
        step_interaction_name: stepInteractionConstants.STEP_INTERACTION_CLICK,
    });
}

export function trackAddressSelection({
    isNegotiableOrder,
    isUserComesFromEmail,
    isUserComesFromAcceptedOffer,
    addressType,
}: {
    isNegotiableOrder: boolean;
    isUserComesFromEmail: boolean;
    isUserComesFromAcceptedOffer: boolean;
    addressType: string;
}): void {
    const checkoutTrigger = getCheckoutTrigger(
        isNegotiableOrder,
        isUserComesFromEmail,
        isUserComesFromAcceptedOffer
    );
    trackEvent({
        eventName: eventNameConstants.EVENT_CHECKOUT_INTERACTION,
        interaction_type: 'address',
        step_interaction_name: 'address option clicked',
        step_type: addressType,
        trigger: checkoutTrigger,
    });
}

export function trackModificationAction({
    isNegotiableOrder,
    isUserComesFromEmail,
    isUserComesFromAcceptedOffer,
    actionName,
    pane,
}: {
    isNegotiableOrder: boolean;
    isUserComesFromEmail: boolean;
    isUserComesFromAcceptedOffer: boolean;
    actionName: string;
    pane: Pane;
}): void {
    const checkoutTrigger = getCheckoutTrigger(
        isNegotiableOrder,
        isUserComesFromEmail,
        isUserComesFromAcceptedOffer
    );
    trackEvent({
        eventName: eventNameConstants.EVENT_CHECKOUT_INTERACTION,
        interaction_type: pane,
        step_interaction_name: `${actionName} clicked`,
        trigger: checkoutTrigger,
    });
}

export function trackAddressDeletion({
    isNegotiableOrder,
    isUserComesFromEmail,
    isUserComesFromAcceptedOffer,
    actionName,
}: {
    isNegotiableOrder: boolean;
    isUserComesFromEmail: boolean;
    isUserComesFromAcceptedOffer: boolean;
    actionName: string;
}): void {
    const checkoutTrigger = getCheckoutTrigger(
        isNegotiableOrder,
        isUserComesFromEmail,
        isUserComesFromAcceptedOffer
    );
    trackEvent({
        eventName: eventNameConstants.EVENT_CHECKOUT_INTERACTION,
        interaction_type: 'address',
        step_interaction_name: `address deletion ${actionName}`,
        trigger: checkoutTrigger,
    });
}

export function trackDeliveryMethodSelection({
    deliveryMethod,
    isNegotiableOrder,
    isUserComesFromEmail,
    isUserComesFromAcceptedOffer,
}: {
    deliveryMethod: string;
    isNegotiableOrder: boolean;
    isUserComesFromEmail: boolean;
    isUserComesFromAcceptedOffer: boolean;
}): void {
    const checkoutTrigger = getCheckoutTrigger(
        isNegotiableOrder,
        isUserComesFromEmail,
        isUserComesFromAcceptedOffer
    );
    trackEvent({
        eventName: eventNameConstants.EVENT_CHECKOUT_INTERACTION,
        interaction_type: 'shipping',
        step_interaction_name: 'shipping option clicked',
        trigger: checkoutTrigger,
        step_type: deliveryMethod,
    });
}

export function trackWorryFreeReturnsDetails(): void {
    trackEvent({
        eventName: eventNameConstants.EVENT_CHECKOUT_INTERACTION,
        interaction_type: interactionTypeConstants.INTERACTION_TYPE_RETURN_POLICY,
        step_interaction_name: stepInteractionConstants.STEP_INTERACTION_CLICK,
        trigger: 'checkout',
    });
}

export function trackAddSpecialInstructionsClick({
    isNegotiableOrder,
    isUserComesFromEmail,
    isUserComesFromAcceptedOffer,
    isOnDeliveryPane,
}: {
    isNegotiableOrder: boolean;
    isUserComesFromEmail: boolean;
    isUserComesFromAcceptedOffer: boolean;
    isOnDeliveryPane: boolean;
}): void {
    const checkoutTrigger = getCheckoutTrigger(
        isNegotiableOrder,
        isUserComesFromEmail,
        isUserComesFromAcceptedOffer
    );
    trackEvent({
        eventName: eventNameConstants.EVENT_CHECKOUT_INTERACTION,
        interaction_type: isOnDeliveryPane ? 'shipping' : 'review',
        step_interaction_name: 'add special instructions clicked',
        trigger: checkoutTrigger,
    });
}

export function trackChangeLinkClick({
    isNegotiableOrder,
    isUserComesFromEmail,
    isUserComesFromAcceptedOffer,
    step,
    currentPane,
}: {
    isNegotiableOrder: boolean;
    isUserComesFromEmail: boolean;
    isUserComesFromAcceptedOffer: boolean;
    step: string;
    currentPane: string;
}): void {
    let changeStep = step;
    if (step === 'shipping') {
        changeStep = 'address';
    } else if (step === 'delivery') {
        changeStep = 'shipping';
    }

    const checkoutTrigger = getCheckoutTrigger(
        isNegotiableOrder,
        isUserComesFromEmail,
        isUserComesFromAcceptedOffer
    );
    trackEvent({
        eventName: eventNameConstants.EVENT_CHECKOUT_INTERACTION,
        interaction_type: currentPane,
        step_interaction_name: `change ${changeStep} clicked`,
        trigger: checkoutTrigger,
    });
}

export function trackBillingAddressSameAsShipping({
    isNegotiableOrder,
    isUserComesFromEmail,
    isUserComesFromAcceptedOffer,
    isChecked,
}: {
    isNegotiableOrder: boolean;
    isUserComesFromEmail: boolean;
    isUserComesFromAcceptedOffer: boolean;
    isChecked: boolean;
}): void {
    const checkoutTrigger = getCheckoutTrigger(
        isNegotiableOrder,
        isUserComesFromEmail,
        isUserComesFromAcceptedOffer
    );
    trackEvent({
        eventName: eventNameConstants.EVENT_CHECKOUT_INTERACTION,
        interaction_type: 'payment',
        step_interaction_name: 'billing address same as shipping address clicked',
        trigger: checkoutTrigger,
        step_type: isChecked ? 'true' : 'false',
    });
}

function getPaymentMethodType(paymentMethodType: string): string {
    let paymentType;
    if (paymentMethodType === 'CREDIT_CARD') {
        paymentType = 'card';
    } else if (paymentMethodType === 'ACH') {
        paymentType = 'us bank account';
    } else if (
        paymentMethodType === 'WIRE_TRANSFER' ||
        paymentMethodType === 'ON_PLATFORM_WIRE_TRANSFER'
    ) {
        paymentType = 'wire transfer';
    } else if (paymentMethodType === 'APPLE_PAY') {
        paymentType = 'apple pay';
    } else if (paymentMethodType === 'PAYPAL_ACCOUNT') {
        paymentType = 'paypal';
    } else {
        paymentType = paymentMethodType?.toLowerCase();
    }
    return paymentType;
}

export function trackPaymentDeletion({
    isNegotiableOrder,
    isUserComesFromEmail,
    isUserComesFromAcceptedOffer,
    actionName,
    paymentMethodType,
    isNew,
}: {
    isNegotiableOrder: boolean;
    isUserComesFromEmail: boolean;
    isUserComesFromAcceptedOffer: boolean;
    actionName: string;
    paymentMethodType: string;
    isNew: boolean;
}): void {
    const checkoutTrigger = getCheckoutTrigger(
        isNegotiableOrder,
        isUserComesFromEmail,
        isUserComesFromAcceptedOffer
    );
    const paymentType = getPaymentMethodType(paymentMethodType);
    trackEvent({
        eventName: eventNameConstants.EVENT_CHECKOUT_INTERACTION,
        interaction_type: 'payment',
        step_interaction_name: `payment deletion ${actionName}`,
        trigger: checkoutTrigger,
        step_type: `${isNew ? '' : 'existing '}${paymentType}`,
    });
}

export function trackPaymentMethodSelection({
    isNegotiableOrder,
    isUserComesFromEmail,
    isUserComesFromAcceptedOffer,
    paymentMethodType,
    isNew,
}: {
    isNegotiableOrder: boolean;
    isUserComesFromEmail: boolean;
    isUserComesFromAcceptedOffer: boolean;
    paymentMethodType: string;
    isNew: boolean;
}): void {
    const checkoutTrigger = getCheckoutTrigger(
        isNegotiableOrder,
        isUserComesFromEmail,
        isUserComesFromAcceptedOffer
    );
    const paymentType = getPaymentMethodType(paymentMethodType);

    trackEvent({
        eventName: eventNameConstants.EVENT_CHECKOUT_INTERACTION,
        interaction_type: 'payment',
        step_interaction_name: 'payment option clicked',
        trigger: checkoutTrigger,
        step_type: `${isNew ? '' : 'existing '}${paymentType}`,
    });
}

export function trackPaymentModificationAction({
    isNegotiableOrder,
    isUserComesFromEmail,
    isUserComesFromAcceptedOffer,
    actionName,
    pane,
    paymentMethodType,
    isNew,
}: {
    isNegotiableOrder: boolean;
    isUserComesFromEmail: boolean;
    isUserComesFromAcceptedOffer: boolean;
    actionName: string;
    pane: Pane;
    paymentMethodType: string;
    isNew: boolean;
}): void {
    const checkoutTrigger = getCheckoutTrigger(
        isNegotiableOrder,
        isUserComesFromEmail,
        isUserComesFromAcceptedOffer
    );
    const paymentType = getPaymentMethodType(paymentMethodType);

    trackEvent({
        eventName: eventNameConstants.EVENT_CHECKOUT_INTERACTION,
        interaction_type: pane,
        step_interaction_name: `${actionName} clicked`,
        trigger: checkoutTrigger,
        step_type: `${isNew ? '' : 'existing '}${paymentType}`,
    });
}

export const trackAbandonOfferModalShown = ({
    isNegotiableOrder,
    isUserComesFromEmail,
    isUserComesFromAcceptedOffer,
}: {
    isNegotiableOrder: boolean;
    isUserComesFromEmail: boolean;
    isUserComesFromAcceptedOffer: boolean;
}): void => {
    const checkoutTrigger = getCheckoutTrigger(
        isNegotiableOrder,
        isUserComesFromEmail,
        isUserComesFromAcceptedOffer
    );
    trackEvent({
        eventName: eventNameConstants.EVENT_CHECKOUT_INTERACTION,
        interaction_type: interactionTypeConstants.INTERACTION_TYPE_CHECKOUT_ABANDON_PAGE,
        step_interaction_name: 'displayed',
        trigger: checkoutTrigger,
    });
};

export const trackGiftingCheckbox = ({
    isNegotiableOrder,
    isUserComesFromEmail,
    isUserComesFromAcceptedOffer,
    checked,
}: {
    isNegotiableOrder: boolean;
    isUserComesFromEmail: boolean;
    isUserComesFromAcceptedOffer: boolean;
    checked: boolean;
}): void => {
    const checkoutTrigger = getCheckoutTrigger(
        isNegotiableOrder,
        isUserComesFromEmail,
        isUserComesFromAcceptedOffer
    );
    trackEvent({
        eventName: eventNameConstants.EVENT_CHECKOUT_INTERACTION,
        interaction_type: 'review',
        step_interaction_name: 'this order is a gift clicked',
        trigger: checkoutTrigger,
        step_type: String(checked),
    });
};
